import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  BookOpen,
  FileText,
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  Play,
  Upload,
  Mic,
  Edit3,
  Download,
  CheckCircle,
  Users,
  Shield,
  Lock,
  Clock,
  Calendar,
  Bell,
  Settings,
  Zap,
  Award,
  Target,
  AlertTriangle,
  Eye,
  Pencil,
  Send,
  Archive,
  Search,
  Filter,
  BarChart3,
  Layers,
  Link,
  Vote,
  Scale,
  Folder,
  Hash,
} from "lucide-react";

export default function UserGuidePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("introduction");

  useEffect(() => {
    // Handle hash navigation
    const hash = location.hash.replace("#", "");
    if (hash) {
      // Use requestAnimationFrame to avoid cascading render warning
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
      // Defer state update to next tick
      requestAnimationFrame(() => {
        setActiveSection(hash);
      });
    }
  }, [location]);

  const sections = [
    { id: "introduction", title: "Introduction", icon: BookOpen },
    { id: "quick-start", title: "Quick Start", icon: Play },
    { id: "creating-meetings", title: "Creating Meetings", icon: Calendar },
    { id: "audio-transcription", title: "Audio & Transcription", icon: Mic },
    { id: "minutes-generation", title: "Minutes Generation", icon: FileText },
    { id: "action-items", title: "Action Items", icon: CheckCircle },
    { id: "governance", title: "Governance Features", icon: Shield },
    { id: "exports", title: "Exports & Sharing", icon: Download },
    { id: "team-management", title: "Team Management", icon: Users },
    { id: "settings", title: "Settings & Preferences", icon: Settings },
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/docs")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Docs
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-teal-600" />
                <span className="font-semibold text-slate-800">User Guide</span>
              </div>
            </div>
            <Button onClick={() => navigate("/login")}>
              Get Started
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-[280px,1fr] gap-8">
          {/* Sidebar Navigation */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-800 mb-4">Contents</h3>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeSection === section.id
                        ? "bg-teal-50 text-teal-700 font-medium"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <section.icon className="w-4 h-4" />
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="min-w-0">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="p-8 md:p-12">
                {/* Introduction */}
                <section id="introduction" className="mb-16">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-slate-800">User Guide</h1>
                      <p className="text-slate-500">Complete guide to MyMeetingsPal</p>
                    </div>
                  </div>

                  <div className="prose prose-slate max-w-none">
                    <p className="text-lg text-slate-600 leading-relaxed">
                      Welcome to <strong>MyMeetingsPal</strong> — the comprehensive corporate minutes platform 
                      designed for modern governance teams. Whether you&apos;re a small startup or a public company 
                      requiring strict compliance, this guide will help you master every feature.
                    </p>

                    <div className="mt-8 grid sm:grid-cols-3 gap-4">
                      <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
                        <Zap className="w-8 h-8 text-teal-600 mb-2" />
                        <h4 className="font-semibold text-slate-800">AI-Powered</h4>
                        <p className="text-sm text-slate-600">Automated transcription and minutes generation</p>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                        <Shield className="w-8 h-8 text-blue-600 mb-2" />
                        <h4 className="font-semibold text-slate-800">Governance-Ready</h4>
                        <p className="text-sm text-slate-600">Built for public company compliance</p>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                        <Users className="w-8 h-8 text-purple-600 mb-2" />
                        <h4 className="font-semibold text-slate-800">Team Collaboration</h4>
                        <p className="text-sm text-slate-600">Work together seamlessly</p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Quick Start */}
                <section id="quick-start" className="mb-16 pt-8 border-t border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                    <Play className="w-6 h-6 text-emerald-500" />
                    Quick Start Guide
                  </h2>

                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-emerald-600">1</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 mb-2">Create Your Account</h3>
                        <p className="text-slate-600">
                          Sign up with your email and company details. You&apos;ll get a 14-day free trial 
                          of Premium Governance features — no credit card required.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-emerald-600">2</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 mb-2">Set Up Your First Meeting</h3>
                        <p className="text-slate-600">
                          From the dashboard, click &ldquo;New Meeting&rdquo; and fill in the meeting details: 
                          title, date, attendees, and meeting type (Board, Committee, AGM, etc.).
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-emerald-600">3</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 mb-2">Upload Audio or Enter Notes</h3>
                        <p className="text-slate-600">
                          Upload your meeting recording (MP3, WAV, M4A) for automatic transcription, 
                          or enter your notes directly. Our AI will process the content.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-emerald-600">4</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 mb-2">Generate & Review Minutes</h3>
                        <p className="text-slate-600">
                          Click &ldquo;Generate Minutes&rdquo; to create professional corporate minutes. Review, 
                          edit, and refine the content using our rich text editor.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-emerald-600">5</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 mb-2">Export & Share</h3>
                        <p className="text-slate-600">
                          Export your minutes as DOCX or PDF. Share with attendees via email, or use 
                          governance features to get Chair approval and lock the document.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Creating Meetings */}
                <section id="creating-meetings" className="mb-16 pt-8 border-t border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                    <Calendar className="w-6 h-6 text-blue-500" />
                    Creating Meetings
                  </h2>

                  <div className="prose prose-slate max-w-none">
                    <h3>Meeting Types</h3>
                    <p>MyMeetingsPal supports various meeting types, each with appropriate templates:</p>
                    <ul>
                      <li><strong>Board Meetings</strong> — Full board of directors meetings</li>
                      <li><strong>Committee Meetings</strong> — Audit, Remuneration, Nomination committees</li>
                      <li><strong>AGM/EGM</strong> — Annual and Extraordinary General Meetings</li>
                      <li><strong>Executive Meetings</strong> — Leadership team meetings</li>
                      <li><strong>Department Meetings</strong> — Team and department standups</li>
                    </ul>

                    <h3>Meeting Details</h3>
                    <p>When creating a meeting, you&apos;ll specify:</p>
                    <ul>
                      <li><strong>Title</strong> — A descriptive name for the meeting</li>
                      <li><strong>Date & Time</strong> — When the meeting occurred or will occur</li>
                      <li><strong>Location</strong> — Physical address or &ldquo;Virtual&rdquo; for online meetings</li>
                      <li><strong>Attendees</strong> — List of participants with their roles (Chair, Secretary, Member)</li>
                      <li><strong>Agenda Items</strong> — Topics to be discussed</li>
                    </ul>

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 my-6">
                      <div className="flex items-start gap-3">
                        <Target className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-blue-900 mb-1">Pro Tip</h4>
                          <p className="text-blue-800 text-sm mb-0">
                            Use meeting templates to standardize your agenda items and save time. 
                            Templates can be created in Settings → Meeting Templates.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Audio & Transcription */}
                <section id="audio-transcription" className="mb-16 pt-8 border-t border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                    <Mic className="w-6 h-6 text-rose-500" />
                    Audio & Transcription
                  </h2>

                  <div className="prose prose-slate max-w-none">
                    <h3>Supported Formats</h3>
                    <p>Upload recordings in any of these formats:</p>
                    <ul>
                      <li>MP3, MP4, M4A (most common)</li>
                      <li>WAV, WEBM, OGG</li>
                      <li>Maximum file size: 500MB</li>
                      <li>Maximum duration: 4 hours</li>
                    </ul>

                    <h3>Transcription Process</h3>
                    <ol>
                      <li>Upload your audio file from the meeting detail page</li>
                      <li>Our AI processes the audio (usually 2-5 minutes)</li>
                      <li>Review the transcript for accuracy</li>
                      <li>Edit any misheard words or names</li>
                    </ol>

                    <h3>Speaker Identification</h3>
                    <p>
                      The system attempts to identify different speakers in the recording. 
                      You can manually assign names to speakers for better minute clarity.
                    </p>

                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 my-6">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-amber-900 mb-1">Recording Tips</h4>
                          <p className="text-amber-800 text-sm mb-0">
                            For best transcription results, use a central microphone, minimize 
                            background noise, and ask speakers to identify themselves when speaking.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Minutes Generation */}
                <section id="minutes-generation" className="mb-16 pt-8 border-t border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                    <FileText className="w-6 h-6 text-indigo-500" />
                    Minutes Generation
                  </h2>

                  <div className="prose prose-slate max-w-none">
                    <h3>AI-Powered Generation</h3>
                    <p>
                      Our AI analyzes your transcript or notes and generates professionally formatted 
                      corporate minutes. The generated content includes:
                    </p>
                    <ul>
                      <li>Meeting header with date, time, location, and attendees</li>
                      <li>Quorum verification statement</li>
                      <li>Structured discussion summaries by agenda item</li>
                      <li>Clearly marked resolutions and motions</li>
                      <li>Action items with assignees and due dates</li>
                      <li>Next meeting date and adjournment time</li>
                    </ul>

                    <h3>Editing Minutes</h3>
                    <p>
                      Use our rich text editor to refine the generated minutes:
                    </p>
                    <ul>
                      <li>Format text with bold, italic, underline</li>
                      <li>Create numbered and bulleted lists</li>
                      <li>Add tables for attendance or voting records</li>
                      <li>Insert section headers and dividers</li>
                      <li>Track changes with version history (Premium)</li>
                    </ul>

                    <h3>Version History</h3>
                    <p>
                      Premium plans include full version history. Every save creates a new version, 
                      allowing you to compare changes and restore previous versions if needed.
                    </p>
                  </div>
                </section>

                {/* Action Items */}
                <section id="action-items" className="mb-16 pt-8 border-t border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    Action Items
                  </h2>

                  <div className="prose prose-slate max-w-none">
                    <h3>Automatic Extraction</h3>
                    <p>
                      MyMeetingsPal automatically identifies and extracts action items from your 
                      meeting content. Each action item includes:
                    </p>
                    <ul>
                      <li>Task description</li>
                      <li>Assigned person</li>
                      <li>Due date</li>
                      <li>Priority level</li>
                      <li>Source meeting reference</li>
                    </ul>

                    <h3>Tracking & Follow-up</h3>
                    <p>Track action items through their lifecycle:</p>
                    <ul>
                      <li><strong>Open</strong> — Newly created, awaiting action</li>
                      <li><strong>In Progress</strong> — Work has begun</li>
                      <li><strong>Completed</strong> — Task finished</li>
                      <li><strong>Deferred</strong> — Postponed to a later date</li>
                    </ul>

                    <h3>Cross-Meeting Action Register (Premium)</h3>
                    <p>
                      Premium Governance includes a cross-meeting Action Register that consolidates 
                      all action items across meetings. Features include:
                    </p>
                    <ul>
                      <li>Carry-forward to &ldquo;Matters Arising&rdquo; in subsequent meetings</li>
                      <li>Filtering by status, assignee, or due date</li>
                      <li>Overdue item notifications</li>
                      <li>Export for board reporting</li>
                    </ul>
                  </div>
                </section>

                {/* Governance Features */}
                <section id="governance" className="mb-16 pt-8 border-t border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                    <Shield className="w-6 h-6 text-purple-500" />
                    Governance Features (Premium)
                  </h2>

                  <div className="prose prose-slate max-w-none">
                    <p className="text-lg">
                      Premium Governance features are designed for public companies and organizations 
                      requiring strict compliance and audit trails.
                    </p>

                    <div className="grid sm:grid-cols-2 gap-4 my-8 not-prose">
                      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                        <Lock className="w-8 h-8 text-purple-600 mb-3" />
                        <h4 className="font-semibold text-slate-800 mb-2">Chair Signing & Locking</h4>
                        <p className="text-sm text-slate-600">
                          Digital signature from the Chair creates an immutable record. 
                          Locked minutes cannot be edited.
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                        <Eye className="w-8 h-8 text-blue-600 mb-3" />
                        <h4 className="font-semibold text-slate-800 mb-2">Audit Log</h4>
                        <p className="text-sm text-slate-600">
                          Complete audit trail of all actions: views, edits, exports, 
                          and approvals with timestamps.
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                        <AlertTriangle className="w-8 h-8 text-amber-600 mb-3" />
                        <h4 className="font-semibold text-slate-800 mb-2">Governance Linting</h4>
                        <p className="text-sm text-slate-600">
                          Automated compliance checks for quorum, required sections, 
                          and governance best practices.
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                        <Vote className="w-8 h-8 text-green-600 mb-3" />
                        <h4 className="font-semibold text-slate-800 mb-2">Resolutions & Voting</h4>
                        <p className="text-sm text-slate-600">
                          Track resolutions with formal voting records: proposed, 
                          seconded, and vote counts.
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                        <Scale className="w-8 h-8 text-rose-600 mb-3" />
                        <h4 className="font-semibold text-slate-800 mb-2">Declarations Register</h4>
                        <p className="text-sm text-slate-600">
                          Track conflicts of interest and related party 
                          declarations for each meeting.
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                        <Folder className="w-8 h-8 text-teal-600 mb-3" />
                        <h4 className="font-semibold text-slate-800 mb-2">Board Pack Export</h4>
                        <p className="text-sm text-slate-600">
                          Export complete board packs as ZIP: minutes, action 
                          register, attendance, and resolutions.
                        </p>
                      </div>
                    </div>

                    <h3>Approval Workflow</h3>
                    <ol>
                      <li><strong>Draft</strong> — Secretary prepares minutes</li>
                      <li><strong>Review</strong> — Circulated to attendees for comments</li>
                      <li><strong>Approval</strong> — Chair reviews and approves</li>
                      <li><strong>Signed</strong> — Chair digitally signs</li>
                      <li><strong>Locked</strong> — Minutes become immutable</li>
                    </ol>
                  </div>
                </section>

                {/* Exports */}
                <section id="exports" className="mb-16 pt-8 border-t border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                    <Download className="w-6 h-6 text-cyan-500" />
                    Exports & Sharing
                  </h2>

                  <div className="prose prose-slate max-w-none">
                    <h3>Export Formats</h3>
                    <ul>
                      <li><strong>DOCX</strong> — Microsoft Word format, fully editable</li>
                      <li><strong>PDF</strong> — Portable document, ideal for archiving</li>
                      <li><strong>Board Pack ZIP</strong> (Premium) — Complete package with all related documents</li>
                    </ul>

                    <h3>Sharing Options</h3>
                    <ul>
                      <li><strong>Email Distribution</strong> — Send directly to attendees</li>
                      <li><strong>Secure Link</strong> — Generate a time-limited access link</li>
                      <li><strong>Print</strong> — Optimized print layout</li>
                    </ul>

                    <h3>Retention & Storage</h3>
                    <p>
                      All documents are stored securely with configurable retention policies. 
                      Premium plans include legal hold capabilities to prevent document destruction 
                      during litigation.
                    </p>
                  </div>
                </section>

                {/* Team Management */}
                <section id="team-management" className="mb-16 pt-8 border-t border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                    <Users className="w-6 h-6 text-orange-500" />
                    Team Management
                  </h2>

                  <div className="prose prose-slate max-w-none">
                    <h3>User Roles</h3>
                    <ul>
                      <li><strong>Admin</strong> — Full access, can manage users and settings</li>
                      <li><strong>Secretary</strong> — Can create and edit all meetings</li>
                      <li><strong>Member</strong> — Can view meetings they&apos;re invited to</li>
                      <li><strong>Chair</strong> — Can approve and sign minutes</li>
                    </ul>

                    <h3>Inviting Team Members</h3>
                    <p>
                      Navigate to Settings → Team Members to invite colleagues. They&apos;ll receive 
                      an email invitation to join your organization.
                    </p>

                    <h3>Role-Based Access Control (Enterprise)</h3>
                    <p>
                      Enterprise plans include a full RBAC matrix allowing granular control over 
                      who can view, edit, approve, and export documents.
                    </p>
                  </div>
                </section>

                {/* Settings */}
                <section id="settings" className="mb-8 pt-8 border-t border-slate-100">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                    <Settings className="w-6 h-6 text-slate-500" />
                    Settings & Preferences
                  </h2>

                  <div className="prose prose-slate max-w-none">
                    <h3>Company Settings</h3>
                    <ul>
                      <li>Company name and logo</li>
                      <li>Default timezone</li>
                      <li>Branding colors for exports</li>
                      <li>Email notification preferences</li>
                    </ul>

                    <h3>Meeting Templates</h3>
                    <p>
                      Create reusable templates for different meeting types. Templates can include:
                    </p>
                    <ul>
                      <li>Standard agenda items</li>
                      <li>Default attendee roles</li>
                      <li>Recurring schedule</li>
                      <li>Required governance checks</li>
                    </ul>

                    <h3>Integrations</h3>
                    <ul>
                      <li><strong>Calendar Sync</strong> — Connect Google or Outlook calendars</li>
                      <li><strong>Slack/Teams</strong> — Action item notifications</li>
                      <li><strong>Email</strong> — SendGrid integration for distribution</li>
                    </ul>
                  </div>
                </section>

                {/* CTA */}
                <div className="mt-12 p-8 bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl text-center">
                  <h3 className="text-2xl font-bold text-white mb-2">Ready to Get Started?</h3>
                  <p className="text-teal-100 mb-6">
                    Join thousands of governance teams using MyMeetingsPal.
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <Button
                      onClick={() => navigate("/login")}
                      className="bg-white text-teal-600 hover:bg-teal-50"
                    >
                      Start Free Trial
                    </Button>
                    <Button
                      variant="outline"
                      className="border-white text-white hover:bg-teal-600"
                      onClick={() => navigate("/pricing")}
                    >
                      View Pricing
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
